/**
 * Flags are referred to by their original kebab-cased name.
 * This is for simplicity and standardization across the Node and React SDKs, but requires
 * opting out of the React SDK's default camelCase naming convention.
 * Ensure `useCamelCaseFlagKeys` is off in the relevant flag providers.
 *
 * Flags may be located across different LaunchDarkly projects:
 * @see https://app.launchdarkly.com/projects/default/flags
 * @see https://app.launchdarkly.com/projects/jane-manager/flags
 * @see https://app.launchdarkly.com/projects/jane-brand/flags
 */
export const FLAGS = {
  // ---Brands + Catalog Flags---
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/analytics-cookieless-token-auth */
  analyticsCookielessTokenAuth: 'analytics-cookieless-token-auth',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/dm-custom-image-ads */
  dmCustomImageAds: 'dm-custom-image-ads',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/gg-gold-manager
   * @see https://app.launchdarkly.com/projects/jane-brand/flags/gg-gold-manager
   */
  goldManager: 'gg-gold-manager',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/jam-flight-edit */
  jamFlightEdit: 'jam-flight-edit',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/jam-bid-modifiers */
  jamBidControl: 'jam-bid-modifiers',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/jam-campaign-management */
  jamCampaignManagement: 'jam-campaign-management',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/jam-monthly-revenue-goal-type */
  jamMonthlyRevenueGoalType: 'jam-monthly-revenue-goal-type',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/jam-recoup-flight-tools */
  jamRecoupFlightTools: 'jam-recoup-flight-tools',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/pcat-state-specific-views */
  pcatStateViewEnabled: 'pcat-state-specific-views',
  /** @see https://app.launchdarkly.com/projects/jane-brand/flags/pcat-localized-store-count */
  pcatLocalizedStoreCount: 'pcat-localized-store-count',
  // ---Golden Gurls Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/gg-kiosk-custom-image-ads */
  ggCustomImageAds: 'gg-kiosk-custom-image-ads',
  /** @see https://app.launchdarkly.com/projects/default/flags/gg-one-click-checkout */
  ggOneClickCheckout: 'gg-one-click-checkout',
  /** @see https://app.launchdarkly.com/projects/default/flags/gg-iframe-redirects */
  iframeRedirects: 'gg-iframe-redirects',

  // ---Cannabinerds Flags---
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-show-weekend-lookahead-controls */
  cnShowWeekendLookaheadControls: 'cn-show-weekend-lookahead-controls',
  /** @see https://app.launchdarkly.com/projects/default/flags/cn-special-detail-page-nsw */
  cnSpecialDetailPageNsw: 'cn-special-detail-page-nsw',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-show-kiosk-notifications-card */
  cnShowKioskNotificationsCard: 'cn-show-kiosk-notifications-card',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-skip-redundant-debounced-update */
  cnSkipRedundantDebouncedUpdate: 'cn-skip-redundant-debounced-update',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-use-rules-for-menu-rows-fe */
  cnUseRulesForMenuRows: 'cn-use-rules-for-menu-rows-fe',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-store-based-product-review-flow */
  cnStoreBasedProductReviewFlow: 'cn-store-based-product-review-flow',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-global-product-review-flow */
  cnGlobalProductReviewFlow: 'cn-global-product-review-flow',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/cn-bulk-revert-product-images-descriptions
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-bulk-revert-product-images-descriptions
   */
  cnBulkRevertProductImagesDescriptions:
    'cn-bulk-revert-product-images-descriptions',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-bulk-clear-product-potencies */
  cnBulkClearProductPotencies: 'cn-bulk-clear-product-potencies',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/cn-boost-carousel
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-boost-carousel
   */
  cnBoostCarousel: 'cn-boost-carousel',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-allow-copy-conditions-over */
  cnAllowCopyConditionsOver: 'cn-allow-copy-conditions-over',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-retail-org-ui */
  cnRetailOrgUi: 'cn-retail-org-ui',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-use-backend-special-description */
  cnUseBackendSpecialDescription: 'cn-use-backend-special-description',
  /** @see https://app.launchdarkly.com/projects/default/flags/cn-jpsms-alternative-flow */
  cnJpsmsAlternativeFlow: 'cn-jpsms-alternative-flow',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-menu-row-type-fe */
  cnMenuRowTypeFe: 'cn-menu-row-type-fe',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/cn-annex-cloud-integration */
  cnAnnexCloudIntegration: 'cn-annex-cloud-integration',

  // ---Commerce 3 Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-thematic-rows-context */
  c3ThematicRowsContext: 'c3-thematic-rows-context',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-enable-product-alerts-beta */
  c3EnableProductAlertsBeta: 'c3-enable-product-alerts-beta',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-kiosk-device-expiration-redirect-enabled */
  c3KioskDeviceExpirationRedirectEnabled:
    'c3-kiosk-device-expiration-redirect-enabled',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/comm-3-multiple-thematic-rows */
  comm3MultipleThematicRows: 'comm-3-multiple-thematic-rows',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c-3-inventory-analytics-tab */
  c3InventoryAnalyticsTab: 'c-3-inventory-analytics-tab',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-mid-grid-ad-row */
  c3MidGridAdRow: 'c3-mid-grid-ad-row',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-specials-redesign-cart */
  c3SpecialsRedesignCart: 'c3-specials-redesign-cart',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/c3-specials-redesign-menu
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-specials-redesign-menu
   */
  c3SpecialsRedesignMenu: 'c3-specials-redesign-menu',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-boost-apple-oauth */
  c3BoostAppleOauth: 'c3-boost-apple-oauth',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-remove-cart-limits-toggle */
  c3RemoveCartLimitsToggle: 'c3-remove-cart-limits-toggle',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c-3-medical-id-expiration-field */
  c3MedicalIdExpirationField: 'c-3-medical-id-expiration-field',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-marketplace-med-id-expiration */
  c3MarketplaceMedIdExpiration: 'c3-marketplace-med-id-expiration',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-sdk-menu-rows */
  c3SdkMenuRows: 'c3-sdk-menu-rows',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-audit-logs-beta */
  scAuditLogsBeta: 'sc-audit-logs-beta',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-permission-gate */
  c1PermissionGate: 'c1-permission-gate',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-menu-row-conditions-fe */
  scMenuRowConditionsFe: 'sc-menu-row-conditions-fe',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sour-chiesel-store-specific-sms-integration-ui */
  scSmsIntegration: 'sour-chiesel-store-specific-sms-integration-ui',
  /**
   * @see https://app.launchdarkly.com/projects/default/flags/sc-notification-settings-card-access
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-notification-settings-card-access
   */
  scNotificationSettingsCardAccess: 'sc-notification-settings-card-access',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-stacking-settings-for-pos-stores */
  scStackingSettingsPosStores: 'sc-stacking-settings-for-pos-stores',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-spend-threshold-specials-frontend */
  scSpendThresholdSpecialsFrontend: 'sc-spend-threshold-specials-frontend',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/menu-row-subcategory-and-category */
  scMenuRowSubcategoryAndCategory: 'menu-row-subcategory-and-category',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-show-global-boost-menus-page */
  scShowGlobalBoostMenusPage: 'sc-show-global-boost-menus-page',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-weight-values-frontend */
  scWeightValuesFrontend: 'sc-weight-values-frontend',
  /** @see https://app.launchdarkly.com/projects/default/flags/sc-terpene-filters-marketplace */
  scTerpeneFiltersMarketplace: 'sc-terpene-filters-marketplace',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/sc-display-product-non-standard-weight */
  scDisplayProductNonStandardWeight: 'sc-display-product-non-standard-weight',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-rewards-line-item */
  c3RewardsLineItem: 'c3-rewards-line-item',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-ach-fees-on-kiosk */
  c3AchFeesOnKiosk: 'c3-ach-fees-on-kiosk',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-alias-clarification */
  c3AliasClarification: 'c3-alias-clarification',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/batch-product-detail-fetch */
  c3BatchProductDetailFetch: 'c3-batch-product-detail-fetch',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-use-v3-store-specials */
  c3UseV3StoreSpecials: 'c3-use-v3-store-specials',
  /** @see https://app.launchdarkly.com/projects/default/flags/c3-enable-kiosk-qr-code-auth-with-external-url */
  c3EnableKioskQRCodeAuthWithExternalURL:
    'c3-enable-kiosk-qr-code-auth-with-external-url',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c3-enable-kiosk-qr-code-auth-with-external-url-business */
  c3EnableKioskQRCodeAuthWithExternalURLBusiness:
    'c3-enable-kiosk-qr-code-auth-with-external-url-business',

  // ---Commerce 1 Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/c1-compare-products */
  c1CompareProducts: 'c1-compare-products',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-extended-deals-labels */
  c1ExtendedDealsLabels: 'c1-extended-deals-labels',
  /** @see https://app.launchdarkly.com/projects/default/flags/c1-bundle-tag */
  c1BundleTag: 'c1-bundle-tag',
  /**
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-customize-login-prompt
   * @see https://app.launchdarkly.com/projects/default/flags/c1-customize-login-prompt
   */
  c1CustomizeLoginPrompt: 'c1-customize-login-prompt',
  /**
   * @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-multiple-checkout-agreements
   * @see https://app.launchdarkly.com/projects/default/flags/c1-multiple-checkout-agreements
   */
  c1MultipleCheckoutAgreements: 'c1-multiple-checkout-agreements',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-fulfillment-enabled */
  c1FulfillmentV2: 'c1-fulfillment-enabled',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-hidden-weight-filter/targeting */
  c1HiddenWeightFilter: 'c1-hidden-weight-filter',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c1-biz-dash-sso-login */
  c1BizDashSsoLogin: 'c1-biz-dash-sso-login',

  // ---Commerce 2 Flags---
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/c-2-premium-cloudflare-integrations */
  c2PremiumCloudflareIntegrations: 'c-2-premium-cloudflare-integrations',
  /** @see https://app.launchdarkly.com/projects/default/flags/bloom-messaging-for-migrated-users */
  bloomMessagingForMigratedUsers: 'bloom-messaging-for-migrated-users',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/comm-2-embedded-deal-rows */
  comm2EmbeddedDealRows: 'comm-2-embedded-deal-rows',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/comm-2-embedded-menu-rows */
  comm2EmbeddedMenuRows: 'comm-2-embedded-menu-rows',
  /** @see https://app.launchdarkly.com/projects/default/flags/ob-gtag-events */
  obGtagEvents: 'ob-gtag-events',
  /** @see https://app.launchdarkly.com/projects/default/flags/ob-redis-operations */
  obRedisOperations: 'ob-redis-operations',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/ob-customize-premium-footer */
  obCustomizePremiumFooter: 'ob-customize-premium-footer',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/ob-import-premium-footer */
  obImportPremiumFooter: 'ob-import-premium-footer',
  /** @see https://app.launchdarkly.com/projects/default/flags/ob-wizard-banner-enabled */
  obWizardBannerEnabled: 'ob-wizard-banner-enabled',
  /** @see https://app.launchdarkly.com/projects/default/flags/ob-gold-bank-linking */
  obGoldBankLinking: 'ob-gold-bank-linking',

  // ---DM Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/dm-stack-adapt-frontend-pixel-stores-enabled */
  dmStackAdaptStoresEnabled: 'dm-stack-adapt-frontend-pixel-stores-enabled',
  /** @see https://app.launchdarkly.com/projects/default/flags/dm-auto-refresh-enabled */
  dmAutoRefreshEnabled: 'dm-auto-refresh-enabled',
  /** @see https://app.launchdarkly.com/projects/default/flags/dm-virtual-budtender-enabled */
  dmVirtualBudtenderEnabled: 'dm-virtual-budtender-enabled',

  // ---CRM Flag---
  /** @see https://app.launchdarkly.com/projects/default/flags/bb-crm-tracking */
  bbCrmTracking: 'bb-crm-tracking',

  // ---Platform 2 Flags---
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/p2-minimum-spend-for-discounts-ui */
  p2MinimumSpendForDiscountsUi: 'p2-minimum-spend-for-discounts-ui',
  // ---Platform 1 Flags---
  /** @see https://app.launchdarkly.com/projects/default/flags/p1-skip-recaptcha-for-sandbox-stores */
  p1SkipRecaptchaForSandboxStores: 'p1-skip-recaptcha-for-sandbox-stores',
  /** @see https://app.launchdarkly.com/projects/jane-manager/flags/p1-tipping-curbside */
  p1TippingCurbside: 'p-1-tipping-curbside',
} as const;

export type FeatureFlag = (typeof FLAGS)[keyof typeof FLAGS];
